import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSupportingInfoData } from "../hooks/useSupportingInfoData";
import { ColOption, Table, TableRow, usePagination } from "@coolset/cs-ui";
import { cacheKeys } from "config";
import { useQueryClient } from "react-query";
import { format } from "date-fns";
import { supportingInfoClient } from "../client/supportingInfoClient";
import { TitleCell } from "shared/components/Table/TitleCell";
import { AddEditEntityForm, AddEditFormFields, AddEditFormFieldsType } from "shared/components/Form/AddEditEntityForm";

const TitleCellClass = "_d-flex-ali-center-jc-sb-sm-gap title-cell";

export const SupportingInfosTable = (props: SupportingInfosTableProps) => {
  const { isAddingSupportingInfo, onClose } = props;
  const [supportingInfoToEdit, setSupportingInfoToEdit] = useState<string | number | null>(null);
  const [titleValue, setTitleValue] = useState<string>("");
  const [contentValue, setContentValue] = useState<string>("");
  const queryClient = useQueryClient();

  const paginationStore = usePagination("supportingInfoPageSize");
  const { supportingInfos, supportingInfosCount, isLoadingInfos, supportingInfo, isLoadingInfo } =
    useSupportingInfoData(paginationStore.apiParams, supportingInfoToEdit);
  useEffect(() => paginationStore.setRowCount(supportingInfosCount), [paginationStore, supportingInfosCount]);
  useEffect(() => {
    setSupportingInfoToEdit(null);
    onClose();
  }, [paginationStore.apiParams, onClose]);

  const colOptions = useMemo<ColOption[]>(
    () => [
      { label: "Title", width: "minmax(400px, 2fr)" },
      { label: "Created", width: "minmax(200px, 1fr)" },
      { label: "Modified", width: "minmax(200px, 1fr)" },
    ],
    []
  );

  const handleSupportingInfoSelect = useCallback(
    (id: string | number) => {
      setSupportingInfoToEdit(id);
      onClose();
    },
    [setSupportingInfoToEdit, onClose]
  );

  const handleOnCloseForm = useCallback(() => {
    setSupportingInfoToEdit(null);
    onClose();
  }, [setSupportingInfoToEdit, onClose]);

  const handleOnSave = useCallback(async () => {
    if (isAddingSupportingInfo) {
      await supportingInfoClient.createSupportingInfo({ title: titleValue, content: contentValue });
    } else if (supportingInfoToEdit) {
      await supportingInfoClient.updateSupportingInfo(supportingInfoToEdit, {
        title: titleValue,
        content: contentValue,
      });
    }
    queryClient.invalidateQueries(cacheKeys.supportingInfo.getSupportingInfos);
  }, [contentValue, isAddingSupportingInfo, queryClient, supportingInfoToEdit, titleValue]);

  const handleOnDelete = useCallback(async () => {
    if (!supportingInfoToEdit) return;

    await supportingInfoClient.deleteSupportingInfo(supportingInfoToEdit);
    queryClient.invalidateQueries(cacheKeys.supportingInfo.getSupportingInfos);
  }, [queryClient, supportingInfoToEdit]);

  const rows = useMemo<TableRow[]>(
    () => [
      ...(isAddingSupportingInfo
        ? [
            {
              cells: [
                {
                  content: <TitleCell title="New supporting information" />,
                  className: TitleCellClass,
                },
                { content: "-" },
                { content: "-" },
              ],
              isSelected: true,
            },
          ]
        : []),
      ...supportingInfos.map((info) => ({
        cells: [
          {
            content: <TitleCell title={info.title} />,
            className: TitleCellClass,
            onClick: () => handleSupportingInfoSelect(info.id),
          },
          { content: format(new Date(info.createdAt), "dd MMM ''yy h:mm aaa"), isOneLine: true },
          { content: format(new Date(info.updatedAt), "dd MMM ''yy h:mm aaa"), isOneLine: true },
        ],
        isSelected: info.id === supportingInfoToEdit,
      })),
    ],
    [handleSupportingInfoSelect, isAddingSupportingInfo, supportingInfoToEdit, supportingInfos]
  );

  const formFields = useMemo<AddEditFormFields[]>(
    () => [
      {
        title: "Title",
        value: titleValue,
        inputType: AddEditFormFieldsType.Text,
        onChange: setTitleValue,
        isRequired: true,
      },
      {
        title: "Content",
        value: contentValue,
        inputType: AddEditFormFieldsType.Wysiwyg,
        onChange: setContentValue,
        isRequired: true,
      },
    ],
    [contentValue, titleValue]
  );

  useEffect(() => {
    if (isAddingSupportingInfo) {
      setSupportingInfoToEdit(null);
    }
  }, [isAddingSupportingInfo]);

  useEffect(() => {
    if (isAddingSupportingInfo) {
      setTitleValue("");
      setContentValue("");
    } else {
      setTitleValue(supportingInfo?.title || "");
      setContentValue(supportingInfo?.content || "");
    }
  }, [supportingInfo, isAddingSupportingInfo]);

  return (
    <div className="layout-entity-table">
      <div className="table-container">
        <Table colOptions={colOptions} rows={rows} isLoading={isLoadingInfos} paginationStore={paginationStore} />
      </div>
      {(supportingInfoToEdit || isAddingSupportingInfo) && (
        <AddEditEntityForm
          isNew={isAddingSupportingInfo}
          entityId={supportingInfoToEdit}
          isLoading={isLoadingInfo}
          fields={formFields}
          onSave={handleOnSave}
          onClose={handleOnCloseForm}
          onDelete={handleOnDelete}
          newTitle="New supporting information"
          entityTitle={supportingInfo?.title || ""}
        />
      )}
    </div>
  );
};

interface SupportingInfosTableProps {
  isAddingSupportingInfo: boolean;
  onClose: () => void;
}
